import React, { useContext, useState } from "react";
import { Button, FlexContainer, LottieLoadingIndicator, StandardModal, StandardModalActionGroup, StandardModalSection, TextLabel, Toast } from "src/components";
import Add from "src/img/survey/add.png";
import Check from "src/img/survey/blue-check.svg";
import styled from "styled-components";
import Close from "src/img/close.svg";
import CloseCircle from "src/img/survey/close.png";
import { sendTest } from "../../api/emailCampaignsApi";
import { LoadingIndicatorButton } from "src/scenes/Survey/components/Utils";
import { NewCampaignContext } from "./NewCampaignContext";
import { s3PresignedEmailAttachmentUrl, s3PresignedHeaderImageUrl } from "../../api/newCampaignApi";
import { EMAIL_STATUS } from "../../constants";

export const TextInput = styled.input`
    border: ${p => p.border || "1px solid #8291b2"};
    width: 220px;
    max-width: 220px;
    height: 40px;

    border-radius: 7px;
    padding: 0 8px;
    text-align: left;

    ::placeholder {
        color: gray;
        text-align: center;
        text-align: left;
    };
`;

function TestEmail(props) {
    const { setShowTestEmails } = props;
    const {
        campaignName,
        emailSubject,
        attachments,
        emailContent,
        emailImageHeader,
    } = useContext(NewCampaignContext);
    const [testEmails, setTestEmails] = useState([]);
    const [emailToAdd, setEmailToAdd] = useState("");
    const [isAddingEmail, setIsAddingEmail] = useState(false);
    const [testEmailErrors, setTestEmailErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const validateEmails = () => {
        const newErrors = {};
        if (!emailToAdd) {
            newErrors.email = "Email address is required";
        } else {
            // Simple email regex pattern
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(emailToAdd)) {
                newErrors.email = "Please enter a valid email address";
            }
        }
        return newErrors;
    };

    const addTestEmails = () => {
        const newErrors = validateEmails();
        if (Object.keys(newErrors).length > 0) {
            setTestEmailErrors(newErrors); // display errors
            return;
        }
        if (testEmails.includes(emailToAdd)) return setTestEmailErrors({ email: "Email already exist" });
        setTestEmails(prevEmails => [...prevEmails, emailToAdd]);
        setEmailToAdd("");
        setIsAddingEmail(false);
        setTestEmailErrors("");
    };

    const removeTestEmail = (emailToRemove) => {
        setTestEmails(prevEmails =>
            prevEmails.filter(email => email !== emailToRemove)
        );
    };

    const onSendTest = async () => {
        if (testEmails.length < 1) {
            Toast.error("At least one email address must be provided.");
            return;
        }

        setIsLoading(true);
        const payload = {
            campaignName,
            emailSubject,
            emailContent: emailContent,
            testEmailAddresses: testEmails,
            emailStatus: EMAIL_STATUS.SENT
        };

        if (emailImageHeader) {
            const fileExtension = emailImageHeader.type.split("/").pop();
            try {
                const preSignedCategoryImage = await s3PresignedHeaderImageUrl(emailImageHeader, fileExtension, "image");
                const storedUrl = preSignedCategoryImage.storedUrl.split("?")[0];
                payload.headerImageUrl = storedUrl;
            } catch (error) {
                console.log(error);
                return;
            }
        }


        if (attachments.length > 0) {
            payload.attachments = [];

            for (const attachment of attachments) {
                const file = attachment.file;

                if (!file) {
                    payload.attachments.push({
                        filename: attachment.filename,
                        s3Url: attachment.s3Url
                    });
                } else {
                    try {
                        const fileExtension = file.type.split("/").pop();

                        // Determine attachment type / icon
                        let uploadType = "file";

                        if (file.type.startsWith("image/")) {
                            uploadType = "image";
                        } else if (file.type === "application/pdf") {
                            uploadType = "pdf";
                        } else if (
                            file.type.includes("word") ||
                            file.type.includes("officedocument.wordprocessingml")
                        ) {
                            uploadType = "doc";
                        } else if (
                            file.type.includes("excel") ||
                            file.type.includes("spreadsheetml")
                        ) {
                            uploadType = "xls";
                        }

                        const presigned = await s3PresignedEmailAttachmentUrl(
                            file,
                            fileExtension,
                            uploadType
                        );

                        payload.attachments.push({
                            filename: file.name,
                            s3Url: presigned.storedUrl.split("?")[0],
                        });

                    } catch (error) {
                        console.error("Attachment upload failed:", error);
                        return;
                    }
                }
            }
        }

        try {
            const response = await sendTest(payload);
            if (response) setShowTestEmails(false);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.log("Error:", error);
            Toast.error(error.getApiErrorDetails().message);
        }
    };

    return (
        <StandardModal
            open={true}
            onClose={() => setShowTestEmails(false)}
            widthMode="medium"
            title="Send Test Email"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <Button
                        type="button"
                        width="140px"
                        height="38px"
                        border="1px solid #000"
                        color="#000000"
                        onClick={() => setShowTestEmails(false)}
                        style={{ borderRadius: "7px", boxShadow: "none" }}
                    >
                        Cancel
                    </Button>
                    {isLoading ? (
                        <LoadingIndicatorButton width="140px" height="39px">
                            <LottieLoadingIndicator
                                height="2rem"
                                width="2rem"
                                margin="5px"
                                containerHeight="2rem"
                            />
                        </LoadingIndicatorButton>
                    ) : (
                        <Button
                            width="140px"
                            height="38px"
                            color="#FFFFFF"
                            backgroundColor="#171A22"
                            border="1px solid #171A22"
                            onClick={() => onSendTest()}
                            type="button"
                            style={{ borderRadius: "7px", boxShadow: "none" }}
                        >
                            Send
                        </Button>
                    )}
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <TextLabel weight="600">Email</TextLabel>
                <FlexContainer
                    width="auto"
                    height="auto"
                    minHeight="110px"
                    border="1px solid #D8DEEC"
                    borderRadius="7px"
                    marginTop="10px"
                    padding="10px"
                    direction="row"
                    tDirection="row"
                    mDirection="row"
                    wrap="wrap"
                    gap="5px"
                >
                    {testEmails.length > 0 &&
                        testEmails.map((email, index) => {
                            return (
                                <div
                                    key={index}
                                    style={{
                                        width: "fit-content",
                                        height: "20px",
                                        backgroundColor: "#171A22",
                                        borderRadius: "7px",
                                        padding: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "20px",
                                    }}
                                >
                                    <TextLabel color="white" weight="400">
                                        {email}
                                    </TextLabel>
                                    <img
                                        src={Close}
                                        width="12"
                                        height="12"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => removeTestEmail(email)}
                                    />
                                </div>
                            );
                        })}
                    {isAddingEmail ? (
                        <>
                            <FlexContainer direction="column" height="65px">
                                <FlexContainer direction="row" alignItems="center" gap="5px">
                                    <TextInput
                                        onChange={(e) => setEmailToAdd(e.target.value)}
                                        placeholder="Enter email address"
                                        border={testEmailErrors.email ? "1px solid red" : "1px solid #8291b2"}
                                    />
                                    <img src={CloseCircle} width="35" height="35" style={{ cursor: "pointer" }} onClick={() => setIsAddingEmail(false)} />
                                    <img src={Check} width="25" height="25" style={{ cursor: "pointer" }} onClick={addTestEmails} />
                                </FlexContainer>
                                {testEmailErrors.email && <TextLabel size="0.876rem" color="red">{testEmailErrors.email}</TextLabel>}
                            </FlexContainer>
                        </>
                    ) : (
                        testEmails.length < 5 && <div style={{ height: "40px", marginLeft: "5px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <img
                                src={Add}
                                width="30"
                                height="30"
                                style={{ cursor: "pointer" }}
                                onClick={() => setIsAddingEmail(true)}
                            />
                        </div>
                    )}
                </FlexContainer>
                <FlexContainer marginTop="15px">
                    <TextLabel color="#8551AE">Send up to 5 test emails at once.</TextLabel>
                </FlexContainer>
            </StandardModalSection>
        </StandardModal>
    );
}

export default TestEmail;
